import { useMutateInviteUsersToOrgTeam } from '@/hooks/mutation/useMutateInviteUsersToOrgTeam';
import { useMutateSendBatchTeamInvites } from '@/hooks/mutation/useMutateSendBatchTeamInvites';
import { logger } from '@/utils/logger/logger';
import { TEAM_INVITE_SOURCE, type TeamInviteSourceToken } from '@heidi/growth/contracts';
import { RoleCategory } from '@heidi/schema/ml-scribe';
import { useValidateEmail } from '@heidi/utils';
import {
  GET_JOINED_TEAMS,
  useCombinedQueriesTeamGeneralInfo,
  useOrganizationInfo,
  useQueryUser,
} from '@heidi/workspace/data';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

export interface SendInvitesResult {
  successCount: number;
  failedEmails: string[];
}

type JoinedTeamsCache = {
  joined_teams?: Array<{ team_id: string }>;
};

/**
 * `source` is the growth-owned token, not the app's wider `TeamInviteSource`
 * union: `send_team_invitation` is a cross-domain event and this root reports two
 * of its three sources. The app delegate binds the token to the union member
 * (`TEAM_INVITE_SOURCE_BY_TOKEN` in `useMutateSendBatchTeamInvites`), so the
 * third surface's spelling is not reachable from here and every existing caller
 * — which passes either nothing or the `'onboarding_invite_step'` literal —
 * keeps compiling unchanged.
 */
export function usePracticeInvite(
  source: TeamInviteSourceToken = TEAM_INVITE_SOURCE.practiceInviteModal
) {
  const queryClient = useQueryClient();
  const { isValidEmail } = useValidateEmail();
  const { data: userData } = useQueryUser();
  const { organizationDetails, orgRoles, orgTeams } = useOrganizationInfo();
  const { teamDetails, joinedTeamsData } = useCombinedQueriesTeamGeneralInfo();

  const { mutateAsync: inviteToOrg, isPending: isInvitingToOrg } =
    useMutateInviteUsersToOrgTeam();

  const teamName = useMemo(
    () => teamDetails?.team?.name ?? userData?.organisation_info?.name ?? '',
    [teamDetails, userData]
  );

  const { mutateAsync: inviteToTeam, isPending: isInvitingToTeam } =
    useMutateSendBatchTeamInvites(teamName, source);

  const isPending = isInvitingToOrg || isInvitingToTeam;

  const defaultRoleId = useMemo(() => {
    if (!orgRoles || orgRoles.length === 0) return undefined;
    const clinicianRole = orgRoles.find(
      (role) => role.category === RoleCategory.Clinician
    );
    if (clinicianRole) return clinicianRole.id;
    const defaultRole = orgRoles.find((role) => role.is_default);
    return defaultRole?.id ?? orgRoles[0]?.id;
  }, [orgRoles]);

  const hasOrganization = !!organizationDetails?.organization?.id;
  const currentTeamId = useMemo(() => {
    if (hasOrganization && orgTeams && orgTeams.length > 0) {
      return orgTeams[0]?.id;
    }
    return joinedTeamsData?.joined_teams?.[0]?.team_id;
  }, [hasOrganization, orgTeams, joinedTeamsData]);

  const resolveTeamId = useCallback((): string | undefined => {
    if (hasOrganization && orgTeams && orgTeams.length > 0) {
      return orgTeams[0]?.id;
    }
    if (currentTeamId) return currentTeamId;
    // Completion may call sendInvites right after the org-size PATCH that
    // provisions the team — prefer live cache over the render-time memo.
    const joined = queryClient.getQueryData<JoinedTeamsCache>([GET_JOINED_TEAMS]);
    return joined?.joined_teams?.[0]?.team_id;
  }, [currentTeamId, hasOrganization, orgTeams, queryClient]);

  const sendInvites = useCallback(
    async (validEmails: string[]): Promise<SendInvitesResult | null> => {
      try {
        const teamId = resolveTeamId();
        if (!teamId) {
          logger.error(
            'No team found for Practice invite - team should have been auto-created'
          );
          return null;
        }

        if (hasOrganization && organizationDetails?.organization?.id) {
          const response = await inviteToOrg({
            organizationId: organizationDetails.organization.id,
            input: {
              team_id: teamId,
              user_emails: validEmails,
              role_ids: defaultRoleId ? [defaultRoleId] : [],
            },
          });
          const failedEmails = Object.keys(response.data.failed_emails_data);
          return {
            successCount: response.data.success_emails.length,
            failedEmails,
          };
        }

        const response = await inviteToTeam({
          teamId,
          emails: validEmails,
          roleIds: defaultRoleId ? [defaultRoleId] : undefined,
        });
        const failedEmails = Object.keys(response.data.failed_emails_data ?? {});
        return {
          successCount: Object.keys(response.data.success_emails ?? {}).length,
          failedEmails,
        };
      } catch (error) {
        logger.error('Failed to send practice invites:', error);
        return null;
      }
    },
    [
      resolveTeamId,
      hasOrganization,
      organizationDetails,
      inviteToOrg,
      defaultRoleId,
      inviteToTeam,
    ]
  );

  return { sendInvites, isPending, isValidEmail, hasTeam: !!currentTeamId };
}
